<template>
  <va-modal
    id="cancelCallRequestModal"
    ref="cancelCallRequestModal"
    :value="isOpen"
    :cancelText="$t('modals.buttons.cancel')"
    :okText="$t('modals.buttons.submit')"
    @ok="submit"
    @cancel="onCancel"
    size="small"
    :noOutsideDismiss="true"
    >
    <div slot="header" class="modal-title">
      <span>{{ $t('evocall.requests.cancel_header') }}</span>
    </div>

    <form class="evollu-form">

      <div class="row">
        <div class="col-12">
          <label>{{ $t('evocall.requests.created_at') }}:&nbsp;</label>
          <span class="is-bold"> {{ request.created_at | localTime('DD MMMM YYYY HH:mm')  }}</span>
        </div>

        <div class="col-12">
          <label>{{ $t('evocall.requests.facility') }}:&nbsp;</label>
          <span class="is-bold">{{ request.facility ? request.facility.name : '' }}</span>
        </div>

        <div class="col-12">
          <label>{{ this.$t('evocall.requests.creator') }}:&nbsp;</label>
          <span class="is-bold">{{ request.creator ? request.creator.name : '' }}</span>
        </div>

        <div class="col-12">
          <label>{{ this.$t('clinical.appointments.patient_name') }}:&nbsp;</label>
          <span class="is-bold">{{ request.person ? request.person.name : '' }}</span>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <label class="form-label">{{$t('evocall.inputs.cancelation_reason')}}</label>
          <multiselect
            v-model="form.data.cancel_reason"
            :options="requestCancelationReasons"
            track-by="id"
            :customLabel="option => $t(`evocall.requests.cancelation_reasons.${option.id}`)"
            select-label=""
            selected-label=""
            deselect-label=""
            placeholder="---"
            :allow-empty="false"
            :multiple="false"
            :searchable="true"
            >
          </multiselect>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <va-input
            v-model="form.data.cancel_reason_observations"
            :label="$t('evocall.requests.comment')"
            type="textarea"
            :placeholder="this.$t('evocal.requests.cancel_reason_observations_placeholder')"
            @input="form.errors.clear('cancel_reason_observations')"
            :minRows="3"
            :maxRows="8" />
        </div>
        <span class="small text-danger" v-show="form && form.errors.has('cancel_reason_observations')">
          {{ $t(`${form.errors.get('cancel_reason_observations')}`) }}
        </span>
      </div>
    </form>

  </va-modal>
</template>

<script>
import {mapGetters, mapState} from 'vuex'
import Multiselect from 'vue-multiselect'
import { RequestService } from '@/api/EvocallService'
import Errors from '@/utils/CustomNodeErrors'

export default {
  props: {
    service: {
      type: Object,
      required: true
    },
    request: {
      type: Object,
      required: true
    }
  },
  components: {
    Multiselect
  },
  mounted: function () {
    this.isOpen = true
  },
  data: function () {
    return {
      isOpen: false,
      form: {
        data: {
          cancel_reason: null,
          cancel_reason_observations: null
        },
        errors: new Errors()
      }
    }
  },
  computed: {
    waitingRoomModuleKey: function () {
      return `waiting-room-${this.service.slug}`
    },
    ...mapGetters({
      userIsDispenser: 'auth/userIsDispenser'
    }),
    ...mapState({
      evocallService (state) {
        return state.services.find(service => service.url === state.evocall_server) || null
      },
      authClinicalService: function () {
        return this.request ? this.$store.state.services.find(s => s.id === this.request.service_id) : null
      },
      requestCancelationReasons (state, getters) {
        if (this.evocallService.user_permissions.some(perm => (perm.info_type === 100 && perm.access_type === 3))
          && this.authClinicalService.user_permissions.some(perm => perm.info_type === 100 & perm.access_type === 3)) {
          return Object.values(state[`${this.waitingRoomModuleKey}`].requestCancelationReasons)
        }
        else if(this.userIsDispenser) {
          return Object.values(state[`${this.waitingRoomModuleKey}`].requestCancelationReasons).filter(elem => elem.id !== 4 && elem.id !== 5)
        } else {
          return Object.values(state[`${this.waitingRoomModuleKey}`].requestCancelationReasons)
        }

      },
    }),
    canSubmitForm: function () {
      return this.form.data.cancel_reason !== null
    }
  },
  methods: {
    submit: function () {
      if (this.canSubmitForm) {
        const formData = this.getFormData()
        RequestService.cancel(this.request.id, formData)
          .then(request => {
            this.showToast(this.$t('clinical.appointments.cancel_call_request_success'), this.$store.state.successToastOptions)
            this.$emit('request-canceled', { request })
            this.onCancel()
          })
          .catch(errors => {
            this.showToast(this.$t('clinical.appointments.cancel_call_request_failed'), this.$store.state.errorToastOptions)

            if (errors.response && errors.response.status === 422) {
              this.form.errors.record(errors.response.data.errors, 'evocall.requests.cancel_call_errors.')
            }

            if (errors.hasOwnProperty('response') && errors.response.status === 406) {
              this.$emit('operation-not-permited', { errors })
            }
          })
      } else {
        this.showToast(this.$t('evocall.requests.cannot_cancel_request_missing_cancelation_reason'), this.$store.state.errorToastOptions)
      }
    },
    getFormData: function () {
      return {
        cancel_reason_id: this.form.data.cancel_reason ? this.form.data.cancel_reason.id : null,
        cancel_reason_observations: this.form.data.cancel_reason_observations || null
      }
    },
    onCancel: function () {
      this.isOpen = false
      setTimeout(() => {
        this.$emit('modal-closed')
      }, 500)
    }
  }
}
</script>
